import React, { useState } from 'react';
import { CheckCircle2, Copy, Check, Facebook, AlertTriangle } from 'lucide-react';

const Row = ({ label, children }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <div className="text-sm font-semibold text-slate-900 mt-0.5">{children}</div>
  </div>
);

export const CampaignCreatedModal = ({ data, formData, onViewCampaign }) => {
  const [copied, setCopied] = useState(false);
  const campaignId = data?.meta_results?.campaign_id || data?.campaign?.id || '';
  const name = data?.campaign?.name || formData?.campaignName || '';
  const objective = (data?.campaign?.objective || formData?.objective || '').replace('OUTCOME_', '');
  const acc = data?.ad_account || {};
  const accId = acc.id ? (acc.id.startsWith('act_') ? acc.id : `act_${acc.id}`) : '';
  const partial = !!data?.partial;
  const errors = data?.meta_results?.errors || [];

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(campaignId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="campaign-created-modal">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl border border-slate-200 text-slate-900">
        <div className="px-6 pt-7 pb-5 text-center border-b border-slate-200">
          <div className="mx-auto w-10 h-10 rounded-full border-2 border-green-500 flex items-center justify-center">
            <Check className="h-5 w-5 text-green-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold" data-testid="campaign-created-title">
            {partial ? 'Campaign Partially Created' : 'Campaign Created Successfully'}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {partial ? 'Meta created the campaign but some parts failed.' : 'Meta confirmed that your campaign was created.'}
          </p>
          <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-medium ${partial ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
            {partial ? 'Partial creation' : 'Creation successful'}
          </span>
        </div>

        <div className="px-6 py-5 space-y-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold">Campaign Details</h3>
          <Row label="Campaign name"><span data-testid="campaign-created-name">{name}</span></Row>
          <div className="flex items-end justify-between gap-3">
            <Row label="Campaign ID"><span className="font-mono" data-testid="campaign-created-id">{campaignId}</span></Row>
            <button
              type="button"
              onClick={copyId}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-xs font-medium hover:bg-slate-50 transition-colors"
              data-testid="campaign-created-copy-id"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy ID'}
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold">Selected Meta Ad Account</h3>
          <div className="flex items-center gap-3" data-testid="campaign-created-ad-account">
            <Facebook className="h-5 w-5 text-slate-800 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{acc.name || 'Ad Account'}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Ad Account ID: <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">{accId}</span>
              </p>
            </div>
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
          </div>
          <div className="grid grid-cols-2 gap-4 pt-1">
            <Row label="Campaign status">
              <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium" data-testid="campaign-created-status">Paused</span>
            </Row>
            <Row label="Objective"><span data-testid="campaign-created-objective">{objective.charAt(0) + objective.slice(1).toLowerCase()}</span></Row>
          </div>
          {errors.length > 0 && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 space-y-1">
              <p className="flex items-center gap-1 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Issues</p>
              {errors.map((e, i) => <p key={i}>{e}</p>)}
            </div>
          )}
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-xs text-slate-500">Your campaign has been created in the selected Meta ad account. You can now view it in AdGlobe.</p>
          <button
            type="button"
            onClick={onViewCampaign}
            className="w-full py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium transition-colors"
            data-testid="campaign-created-view-btn"
          >
            View Campaign in AdGlobe
          </button>
        </div>
      </div>
    </div>
  );
};
